import React, { useEffect, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { flowRight } from 'lodash';
import DocumentReferenceValueViewerProps from './DocumentReferenceValueViewer.props';
import { StyledFootnote } from './DocumentReferenceValueViewer.styles';
import { useDocumentFootnotes } from '../../../../context/DocumentFootnotesContext';
import { FinancialsCellWithReferences } from '../../../../pages/Financials/types/cell-with-references';
import withDialog from '../../../WithDialog';

const DocumentReferenceValueViewer = ({ cell }: DocumentReferenceValueViewerProps) => {
  const { value, columnarReferences = [], columnarReferencesMetadata = [] } = cell as FinancialsCellWithReferences;
  const { registerFootnote, unregisterFootnote, getSourceNumber, getDocumentMetadata } = useDocumentFootnotes();

  // Create a footnote identifier based on the document IDs
  const footnoteId = columnarReferences.length > 0 ? columnarReferences.sort().join(',') : null;

  useEffect(() => {
    if (footnoteId && columnarReferences.length > 0) {
      // Combine columnarReferences and metadata into DocumentReference[] format
      const documents = columnarReferences.map((docId: number, index: number) => ({
        documentId: docId,
        fields: columnarReferencesMetadata[index]?.fields || [],
      }));
      registerFootnote(footnoteId, documents);

      return () => {
        unregisterFootnote(footnoteId);
      };
    }
  }, [footnoteId, columnarReferences, columnarReferencesMetadata, registerFootnote, unregisterFootnote]);

  // Get source numbers with their document IDs
  const sourceData = useMemo(
    () =>
      columnarReferences
        .map((docId: number) => ({
          docId,
          sourceNum: getSourceNumber(docId),
        }))
        .filter(
          (item: { docId: number; sourceNum: number | undefined }): item is { docId: number; sourceNum: number } =>
            item.sourceNum !== undefined
        )
        .sort(
          (a: { docId: number; sourceNum: number }, b: { docId: number; sourceNum: number }) =>
            a.sourceNum - b.sourceNum
        ),
    [columnarReferences, getSourceNumber]
  );

  // Format tooltip content for a document
  const formatTooltipContent = (docId: number) => {
    const metadata = getDocumentMetadata(docId);
    if (!metadata) {
      return '';
    }

    const lines: string[] = [];

    // Add document name if available
    if (metadata.documentName) {
      lines.push(`Document: ${metadata.documentName}`);
      if (metadata.fields.length > 0) {
        lines.push(''); // Add blank line for separation
      }
    }

    // Add field names
    lines.push(...metadata.fields.map(field => field.fieldName));

    return lines.join('\n');
  };

  if (!columnarReferences || columnarReferences.length === 0) {
    return <span className="text-cell">{value as string}</span>;
  }

  return (
    <span className="value-viewer">
      <div>
        <span className="text-cell">{value as string}</span>
      </div>
      <div>
        {sourceData.map(({ docId, sourceNum }: { docId: number; sourceNum: number }) => (
          <Tooltip
            key={docId}
            title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{formatTooltipContent(docId)}</pre>}>
            <StyledFootnote data-footnote-id={footnoteId} data-document-refs={JSON.stringify(columnarReferences)}>
              [{sourceNum}]
            </StyledFootnote>
          </Tooltip>
        ))}
      </div>
    </span>
  );
};

const enhancements = flowRight(withDialog);
export default enhancements(DocumentReferenceValueViewer);
